import type { ChatMessage } from '@/components/chat/ChatComponents';

/**
 * On-device persistence for a GUEST's in-progress chat. Guests have no
 * server-side conversation, so their chat lives in ``sessionStorage`` and is
 * restored on return. Registered in ``SESSION_PERSISTENT_KEYS`` so it survives
 * an in-session refresh but clears on tab close / sign-out — matching the guest
 * address's privacy-scoped behavior.
 */
const STORAGE_KEY = 'votemate_guest_chat';

export interface GuestChatState {
  messages: ChatMessage[];
  conversationId: string | null;
}

export const readGuestChat = (): GuestChatState | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      !Array.isArray((parsed as GuestChatState).messages)
    ) {
      return null;
    }
    const { messages, conversationId } = parsed as GuestChatState;
    return {
      messages: messages.filter((m) => m && typeof m.id === 'string' && m.sender),
      conversationId: typeof conversationId === 'string' ? conversationId : null,
    };
  } catch {
    return null;
  }
};

export const writeGuestChat = (messages: ChatMessage[], conversationId: string | null): void => {
  try {
    // Drop transient streaming flags so only settled content is stored.
    const clean = messages.map((m) => {
      const copy: ChatMessage = { ...m };
      delete copy.isLoading;
      delete copy.loadingMessage;
      delete copy.narrationText;
      // Reasoning is the largest field on a turn and sessionStorage is a hard
      // quota shared with the rest of the guest conversation, so it is dropped
      // rather than risking a write failure that loses the whole thread. The
      // reasoning stays readable for the life of the tab; a reload settles for
      // the answer and its sources.
      delete copy.narrationEvents;
      return copy;
    });
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ messages: clean, conversationId }));
  } catch {
    // sessionStorage full / disabled (private mode, quota) — fail silently.
  }
};

export const clearGuestChat = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};
